import React, { MouseEventHandler } from "react";
import { useTranslation } from "react-i18next";
import { Member, Organization } from "../common/ObjectTypes";
import { BackArrow } from "../common/ProjectIcons";
import NoResults from "../common/NoResults";
import { HoverTooltip } from "../common/HoverTooltip";
import { containerClasses, tableClasses, textClasses, buttonClasses, skeletonClasses, paginationClasses } from "../common/tailwindClassnames";
import Pagination from "../common/Pagination";

type OrganizationDetailsProps = {
    orgMembers: Member[],
    organization: Organization,
    onBackArrow: MouseEventHandler,
    onClickInvite: MouseEventHandler,
    onClickMember: Function,
    onToggleModal: MouseEventHandler,
    onToggleRemoveMemberModal: Function,
    currentMember: Member,
    superuser: boolean,
    pageLoaded: boolean,
    // `orgMembers` is the current page; these describe the full member list it
    // was sliced from, so the paginator can show real totals.
    totalCount: number,
    offset: number,
    take: number,
    onPageClicked: Function
}

const OrganizationDetails = ({
    orgMembers,
    organization,
    onBackArrow,
    onToggleModal,
    onClickInvite,
    onClickMember,
    onToggleRemoveMemberModal,
    currentMember,
    superuser,
    pageLoaded,
    totalCount,
    offset,
    take,
    onPageClicked
}: OrganizationDetailsProps) => {
    const { t } = useTranslation();
    return (
        <div className={containerClasses.page}>
            <div className="max-w-list-table mx-auto">
                <div className="text-blue-basic flex flex-row text-sm mb-3 cursor-pointer hover:underline w-min transition-colors duration-300"
                    onClick={onBackArrow}>
                    <BackArrow />  <label className="ml-2">{t("Workspaces")}</label>
                </div>
                <div className="mb-3 flex justify-between">
                    <div>
                        <h1 className={textClasses.heading}>{organization.name}</h1>
                    </div>
                    {(currentMember.role === "admin" || superuser) && <div className="flex gap-3">
                        <button className={buttonClasses.lightGreen}
                            onClick={onToggleModal}>
                            {t("Update")}
                        </button>
                        <button className={buttonClasses.primary}
                            onClick={onClickInvite}
                        >
                            {t("Add User")}
                        </button>
                    </div>}
                </div>
                <table className={tableClasses.container}>
                    <thead className={tableClasses.header}>
                        <tr>
                            <th className={tableClasses.headerCell}>
                                {t("Name")}
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Email")}
                            </th>
                            <th className={tableClasses.headerCell}>
                                {t("Role")}
                            </th>
                            {(currentMember.role === "admin" || superuser) && <th className={tableClasses.headerCell}>
                                &nbsp;
                            </th>}
                        </tr>
                    </thead>
                    {!pageLoaded ?
                        <tbody className="text-sm">
                            {[...Array(8)].map((_, i) => (
                                <tr key={i} className="border border-gray-lines bg-white" data-testid="loading">
                                    <td className={tableClasses.cell}><div className={`h-4 w-32 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-48 ${skeletonClasses.base}`} /></td>
                                    <td className={tableClasses.cell}><div className={`h-4 w-16 ${skeletonClasses.base}`} /></td>
                                    {(currentMember.role === "admin" || superuser) && (
                                        <td className={tableClasses.cell}><div className={`h-4 w-6 ${skeletonClasses.base}`} /></td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                        :
                        <tbody className="text-sm transition-colors duration-300">
                            {orgMembers.map((org_member: Member) => {
                                return (
                                    <tr key={org_member.id ? org_member.id.toString() : Math.random()}
                                        className="border border-gray-lines hover:bg-light-blue bg-white transition-colors duration-300">
                                        <td className={tableClasses.cell}
                                            onClick={() => onClickMember(org_member.id)}>
                                            {org_member.full_name}
                                        </td>
                                        <td className={tableClasses.cell}
                                            onClick={() => onClickMember(org_member.id)}>
                                            {org_member.email}
                                        </td>
                                        {/* <td className="p-3">
                                                Non mvp
                                            </td> */}
                                        <td className={tableClasses.cell}
                                            onClick={() => onClickMember(org_member.id)}>
                                            {org_member.role}
                                        </td>
                                        <td className={tableClasses.cell + " text-right"}>
                                            {(currentMember.role === "admin" || superuser) ? (
                                                <button
                                                    type="button"
                                                    className={buttonClasses.lightRed}
                                                    onClick={() => onToggleRemoveMemberModal(org_member.id)}
                                                >
                                                    {t("Remove")}
                                                </button>
                                            ) : (
                                                <HoverTooltip position="top-right" width="w-52" content={t("Only admins can manage workspace membership.")}>
                                                    <button
                                                        type="button"
                                                        disabled
                                                        className={buttonClasses.lightRed}
                                                    >
                                                        {t("Remove")}
                                                    </button>
                                                </HoverTooltip>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>}
                    {pageLoaded && orgMembers.length === 0 &&
                        <tbody><tr><td colSpan={(currentMember.role === "admin" || superuser) ? 4 : 3} className="py-16">
                            <NoResults
                                headerMsg={t("No Members Found")}
                                labelMsg={t("You may need to add your first member, or contact system admin.")}
                            />
                        </td></tr></tbody>}
                </table>
                {totalCount > take && <Pagination
                    showText={true}
                    handlePageClick={onPageClicked}
                    pageCount={Math.ceil(totalCount / take)}
                    resultStart={offset + 1}
                    resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                    totalCount={totalCount}
                    initialPage={Math.floor(offset / take)}
                    take={take}
                    loading={!pageLoaded}
                    mainclassname={paginationClasses.container}
                />}
            </div>
        </div>
    );
};

export default OrganizationDetails;